import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api';
import { getApiErrorData } from '../../services/apiError';
import type { WxbDataTableProps } from '../wxb-ui';
import {
  WxbButton,
  WxbDataTable,
  WxbInput,
  WxbInputNumber,
  WxbModal,
  WxbSpinner,
  WxbSwitch,
  WxbTableActionCell,
  WxbTag,
  WxbTextarea,
  wxbToast,
} from '../wxb-ui';
import { PlusIcon } from './OrgWorkbenchIcons';
import './PositionManagementModal.css';

interface PositionManagementModalProps {
  open: boolean;
  onClose: () => void;
}

interface EmployeeRole {
  id: number;
  roleCode: string;
  roleName: string;
  description: string | null;
  canSchedule: boolean;
  allowedShiftCodes: string | null;
  defaultSkillLevel: number | null;
}

interface PositionFormValues {
  roleCode: string;
  roleName: string;
  description: string;
  canSchedule: boolean;
  allowedShiftCodes: string;
  defaultSkillLevel: number | null;
}

const EMPTY_POSITION_FORM: PositionFormValues = {
  roleCode: '',
  roleName: '',
  description: '',
  canSchedule: true,
  allowedShiftCodes: '',
  defaultSkillLevel: null,
};

const normalizeOptionalText = (value: string): string | null => {
  const normalized = value.trim();
  return normalized || null;
};

const normalizeEmployeeRole = (value: unknown): EmployeeRole | null => {
  const role = value as Record<string, unknown>;
  const id = Number(role?.id);
  const roleCode = String(role?.roleCode ?? role?.role_code ?? '').trim();
  const roleName = String(role?.roleName ?? role?.role_name ?? '').trim();

  if (!Number.isFinite(id) || id <= 0 || !roleCode || !roleName) {
    return null;
  }

  const rawSkillLevel = role.defaultSkillLevel ?? role.default_skill_level;
  const skillLevel = rawSkillLevel === null || rawSkillLevel === undefined || rawSkillLevel === ''
    ? null
    : Number(rawSkillLevel);

  return {
    id,
    roleCode,
    roleName,
    description: normalizeOptionalText(String(role.description ?? '')),
    canSchedule: Boolean(Number(role.canSchedule ?? role.can_schedule ?? 0)),
    allowedShiftCodes: normalizeOptionalText(String(role.allowedShiftCodes ?? role.allowed_shift_codes ?? '')),
    defaultSkillLevel: Number.isFinite(skillLevel) ? skillLevel : null,
  };
};

const toFormValues = (role: EmployeeRole): PositionFormValues => ({
  roleCode: role.roleCode,
  roleName: role.roleName,
  description: role.description ?? '',
  canSchedule: role.canSchedule,
  allowedShiftCodes: role.allowedShiftCodes ?? '',
  defaultSkillLevel: role.defaultSkillLevel,
});

const getErrorMessage = (error: unknown, fallback: string): string => {
  const data = getApiErrorData<{ error?: unknown; message?: unknown }>(error);
  const message = data?.error ?? data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
};

const PositionManagementModal: React.FC<PositionManagementModalProps> = ({ open, onClose }) => {
  const [roles, setRoles] = useState<EmployeeRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<EmployeeRole | null>(null);
  const [formValues, setFormValues] = useState<PositionFormValues>(EMPTY_POSITION_FORM);
  const [formErrors, setFormErrors] = useState<{ roleCode?: string; roleName?: string }>({});

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/organization/roles');
      const normalizedRoles = Array.isArray(response.data)
        ? response.data
          .map(normalizeEmployeeRole)
          .filter((role): role is EmployeeRole => Boolean(role))
        : [];
      setRoles(normalizedRoles);
    } catch (error) {
      wxbToast.error(getErrorMessage(error, '岗位列表加载失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadRoles();
    }
  }, [loadRoles, open]);

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingRole(null);
    setFormValues(EMPTY_POSITION_FORM);
    setFormErrors({});
  };

  const handleClose = () => {
    closeEditor();
    onClose();
  };

  const openCreateEditor = () => {
    setEditingRole(null);
    setFormValues(EMPTY_POSITION_FORM);
    setFormErrors({});
    setEditorOpen(true);
  };

  const openEditEditor = (role: EmployeeRole) => {
    setEditingRole(role);
    setFormValues(toFormValues(role));
    setFormErrors({});
    setEditorOpen(true);
  };

  const handleSave = async () => {
    const roleCode = formValues.roleCode.trim().toUpperCase();
    const roleName = formValues.roleName.trim();
    const errors = {
      roleCode: roleCode ? undefined : '请输入岗位编码',
      roleName: roleName ? undefined : '请输入岗位名称',
    };
    setFormErrors(errors);

    if (errors.roleCode || errors.roleName) return;

    const payload = {
      roleName,
      description: normalizeOptionalText(formValues.description),
      canSchedule: formValues.canSchedule,
      allowedShiftCodes: normalizeOptionalText(formValues.allowedShiftCodes),
      defaultSkillLevel: formValues.defaultSkillLevel,
    };

    setSaving(true);
    try {
      if (editingRole) {
        await api.put(`/organization/roles/${editingRole.id}`, payload);
        wxbToast.success('岗位已更新');
      } else {
        await api.post('/organization/roles', { ...payload, roleCode });
        wxbToast.success('岗位已新增');
      }
      closeEditor();
      await loadRoles();
    } catch (error) {
      wxbToast.error(getErrorMessage(error, editingRole ? '岗位更新失败' : '岗位新增失败'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role: EmployeeRole) => {
    try {
      const response = await api.delete(`/organization/roles/${role.id}`);
      wxbToast.success(response.data?.softDeleted ? '岗位仍被员工使用，已停止参与排班' : '岗位已删除');
      await loadRoles();
    } catch (error) {
      wxbToast.error(getErrorMessage(error, '岗位删除失败'));
    }
  };

  const columns: WxbDataTableProps<EmployeeRole>['columns'] = [
    {
      title: '岗位编码',
      dataIndex: 'roleCode',
      key: 'roleCode',
      width: 160,
      render: (roleCode: string) => <span className="position-config-modal__code">{roleCode}</span>,
    },
    {
      title: '岗位名称',
      dataIndex: 'roleName',
      key: 'roleName',
      width: 180,
      render: (roleName: string) => <span className="position-config-modal__name">{roleName}</span>,
    },
    {
      title: '参与排班',
      dataIndex: 'canSchedule',
      key: 'canSchedule',
      width: 120,
      render: (canSchedule: boolean) => (
        <WxbTag color={canSchedule ? 'green' : 'neutral'}>{canSchedule ? '允许' : '禁止'}</WxbTag>
      ),
    },
    {
      title: '允许班次',
      dataIndex: 'allowedShiftCodes',
      key: 'allowedShiftCodes',
      width: 180,
      render: (allowedShiftCodes: string | null) => allowedShiftCodes || '不限',
    },
    {
      title: '默认技能等级',
      dataIndex: 'defaultSkillLevel',
      key: 'defaultSkillLevel',
      width: 150,
      render: (defaultSkillLevel: number | null) => defaultSkillLevel ?? '—',
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_: unknown, role: EmployeeRole) => (
        <WxbTableActionCell
          actions={[
            { key: 'edit', label: '编辑', onClick: () => openEditEditor(role) },
            {
              key: 'delete',
              label: '删除',
              variant: 'danger',
              onClick: () => handleDelete(role),
              confirm: {
                title: `删除岗位“${role.roleName}”？`,
                description: '若已有员工使用该岗位，系统将停止该岗位参与排班。',
                okText: '删除',
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <WxbModal
        open={open}
        title="岗位配置"
        width={1000}
        onCancel={handleClose}
        footer={(
          <div className="position-config-modal__footer">
            <WxbButton type="button" variant="ghost" onClick={handleClose}>关闭</WxbButton>
          </div>
        )}
      >
        <div className="position-config-modal__toolbar">
          <p className="position-config-modal__hint">配置员工岗位及其排班资格。修改后会在员工编辑中的“岗位”选项生效。</p>
          <WxbButton type="button" variant="primary" onClick={openCreateEditor}>
            <PlusIcon />
            新增岗位
          </WxbButton>
        </div>

        {loading ? (
          <div className="position-config-modal__loading"><WxbSpinner size={28} /></div>
        ) : (
          <WxbDataTable<EmployeeRole>
            rowKey="id"
            columns={columns}
            dataSource={roles}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
            size="middle"
            density="standard"
            emptyState={{ description: '尚未配置岗位' }}
          />
        )}
      </WxbModal>

      <WxbModal
        open={editorOpen}
        title={editingRole ? '编辑岗位' : '新增岗位'}
        width={640}
        onCancel={closeEditor}
        onOk={handleSave}
        okText={editingRole ? '保存更改' : '新增岗位'}
        cancelText="取消"
        confirmLoading={saving}
      >
        <div className="position-config-modal__form-grid">
          <WxbInput
            label="岗位编码"
            value={formValues.roleCode}
            disabled={Boolean(editingRole)}
            error={formErrors.roleCode}
            placeholder="例如：FRONTLINE"
            onChange={(event) => setFormValues((current) => ({ ...current, roleCode: event.target.value }))}
          />
          <WxbInput
            label="岗位名称"
            value={formValues.roleName}
            error={formErrors.roleName}
            placeholder="例如：一线操作员"
            onChange={(event) => setFormValues((current) => ({ ...current, roleName: event.target.value }))}
          />
          <WxbTextarea
            className="position-config-modal__form-full"
            label="岗位说明"
            value={formValues.description}
            placeholder="填写岗位职责或适用范围（可选）"
            onChange={(event) => setFormValues((current) => ({ ...current, description: event.target.value }))}
          />
          <WxbInput
            label="允许班次"
            value={formValues.allowedShiftCodes}
            helpText="多个班次请用英文逗号分隔；留空表示不限。"
            placeholder="例如：DAY,NIGHT"
            onChange={(event) => setFormValues((current) => ({ ...current, allowedShiftCodes: event.target.value }))}
          />
          <WxbInputNumber
            label="默认技能等级"
            min={0}
            precision={0}
            value={formValues.defaultSkillLevel}
            placeholder="可选"
            onChange={(value) => setFormValues((current) => ({
              ...current,
              defaultSkillLevel: typeof value === 'number' ? value : null,
            }))}
          />
          <div className="position-config-modal__schedule-setting">
            <span className="position-config-modal__setting-label">参与排班</span>
            <WxbSwitch
              checked={formValues.canSchedule}
              checkedChildren="允许"
              unCheckedChildren="禁止"
              onChange={(canSchedule) => setFormValues((current) => ({ ...current, canSchedule }))}
            />
            <span className="position-config-modal__setting-help">禁止后，该岗位不会被纳入排班候选人。</span>
          </div>
        </div>
      </WxbModal>
    </>
  );
};

export default PositionManagementModal;
